import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** وميض أبيض خفيف عند تغيير tickKey — للاستخدام فوق أي محتوى (مثل فلاش كارد). */
export function FlashTickOverlay({ tickKey, className = '' }) {
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 110);
    return () => clearTimeout(t);
  }, [tickKey]);
  return (
    <div
      className={`pointer-events-none absolute inset-0 z-10 rounded-2xl bg-white transition-opacity duration-100 ease-out ${
        flash ? 'opacity-[0.18]' : 'opacity-0'
      } ${className}`}
      aria-hidden
    />
  );
}

/**
 * عرض رقم كبير يتقلص تلقائياً ليبقى ضمن العرض، مع وميض عند كل tick (حتى لو نفس الرقم).
 * @param {'dark'|'light'} theme — dark: نص أبيض، light: نص داكن
 * @param {boolean} frameless — بدون min-height كبير ولا وميض خلفية (عرض مباشر يوفر مساحة)
 */
function FullscreenNumberDisplay({ value, tickKey, theme = 'dark', className = '', frameless = false }) {
  const containerRef = useRef(null);
  const [fontSize, setFontSize] = useState(160);
  const [flash, setFlash] = useState(false);

  const str = value != null && value !== '' ? String(value) : '';
  const isLight = theme === 'light';

  useEffect(() => {
    if (frameless) return;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 110);
    return () => clearTimeout(t);
  }, [tickKey, frameless]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !str) return;

    const run = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w < 8) return;
      const len = str.length;
      const widthFactor = len > 10 ? 0.14 : len > 6 ? 0.18 : 0.22;
      const maxCap = frameless ? 220 : 280;
      const maxPx = Math.min(maxCap, w * widthFactor);
      let size = maxPx;
      if (h > 24) {
        const capByHeight = h * (frameless ? 0.62 : 0.52);
        size = Math.min(size, capByHeight);
      }
      const minPx = 18;
      const test = document.createElement('span');
      test.setAttribute('style', 'position:absolute;left:-99999px;top:0;white-space:nowrap;font-weight:700;');
      test.style.fontFamily = getComputedStyle(el).fontFamily || 'inherit';
      test.textContent = str;
      document.body.appendChild(test);
      while (size > minPx) {
        test.style.fontSize = `${size}px`;
        const lineH = size * 1.12;
        const fitsWidth = test.offsetWidth <= w - 4;
        const fitsHeight = !h || h < 20 || lineH <= h - 6;
        if (fitsWidth && fitsHeight) break;
        size -= 2;
      }
      document.body.removeChild(test);
      setFontSize(size);
    };

    run();
    const ro = new ResizeObserver(run);
    ro.observe(el);
    return () => ro.disconnect();
  }, [str, tickKey, frameless]);

  const containerClass = frameless
    ? `relative mx-auto flex w-full items-center justify-center px-0 py-0 min-h-0 ${className}`
    : `relative mx-auto flex w-full max-w-[min(100vw-2rem,56rem)] items-center justify-center px-2 min-h-[min(32vh,220px)] max-h-[min(52vh,520px)] ${className}`;

  return (
    <div ref={containerRef} className={containerClass}>
      {!frameless && (
        <div
          className={`pointer-events-none absolute inset-0 rounded-2xl transition-opacity duration-100 ease-out ${
            isLight ? 'bg-amber-400' : 'bg-white'
          } ${flash ? 'opacity-[0.28]' : 'opacity-0'}`}
          aria-hidden
        />
      )}
      <span
        className={`relative z-10 font-bold tabular-nums leading-none tracking-tight select-none text-center max-w-full ${
          frameless ? 'text-white' : isLight ? 'text-gray-900' : 'text-white'
        }`}
        style={{ fontSize: `${fontSize}px`, whiteSpace: 'nowrap' }}
      >
        {str}
      </span>
    </div>
  );
}

export default FullscreenNumberDisplay;
